import type { SupportedLocale } from "@/i18n-locales";

const route = (
  id: string,
  it: string,
  en: string,
  es: string,
  fr: string,
  de: string,
) => ({ id, slugs: { it, en, es, fr, de } }) as const;

// Keep route recognition separate from the complete tool definitions. Public
// layout components only need an id and a localized slug; loading fields,
// calculators and quote rules in the initial bundle would be wasteful.
export const TOOL_ROUTE_INDEX = [
  route(
    "digital_assistance",
    "sportello-assistenza-digitale",
    "digital-assistance-desk",
    "ventanilla-asistencia-digital",
    "guichet-assistance-numerique",
    "digitale-hilfe",
  ),
  route(
    "gaming_pc",
    "configuratore-pc-gaming",
    "gaming-pc-builder",
    "configurador-pc-gaming",
    "configurateur-pc-gaming",
    "gaming-pc-konfigurator",
  ),
  route(
    "workstation",
    "configuratore-workstation",
    "workstation-builder",
    "configurador-workstation",
    "configurateur-workstation",
    "workstation-konfigurator",
  ),
  route(
    "website_estimator",
    "preventivo-sito-web",
    "website-cost-estimator",
    "estimador-sitio-web",
    "estimateur-site-web",
    "website-kostenrechner",
  ),
  route(
    "ecommerce_estimator",
    "configuratore-ecommerce",
    "ecommerce-estimator",
    "configurador-ecommerce",
    "configurateur-ecommerce",
    "ecommerce-konfigurator",
  ),
  route(
    "digital_maturity",
    "test-maturita-digitale",
    "digital-maturity-test",
    "test-madurez-digital",
    "test-maturite-numerique",
    "digitaler-reifegrad-test",
  ),
  route(
    "social_plan",
    "calcolatore-piano-social",
    "social-plan-calculator",
    "calculadora-plan-social",
    "calculateur-plan-social",
    "social-media-planer",
  ),
  route(
    "advertising_budget",
    "calcolatore-budget-pubblicitario",
    "advertising-budget-calculator",
    "calculadora-presupuesto-publicitario",
    "calculateur-budget-publicitaire",
    "werbebudget-rechner",
  ),
  route(
    "website_audit",
    "audit-rapido-sito-web",
    "quick-website-audit",
    "auditoria-rapida-sitio-web",
    "audit-rapide-site-web",
    "website-schnellcheck",
  ),
  route(
    "slow_website_cost",
    "costo-sito-lento",
    "slow-website-cost",
    "coste-sitio-lento",
    "cout-site-lent",
    "kosten-langsamer-website",
  ),
  route(
    "pc_upgrade",
    "consiglio-upgrade-pc",
    "pc-upgrade-advisor",
    "asesor-actualizacion-pc",
    "conseiller-evolution-pc",
    "pc-upgrade-berater",
  ),
  route(
    "fault_diagnosis",
    "prediagnosi-guasto",
    "fault-pre-diagnosis",
    "prediagnostico-averia",
    "prediagnostic-panne",
    "fehler-vordiagnose",
  ),
  route(
    "repair_estimator",
    "preventivo-riparazione",
    "repair-estimator",
    "estimador-reparacion",
    "estimateur-reparation",
    "reparatur-kostenrechner",
  ),
  route(
    "component_compatibility",
    "verifica-compatibilita-componenti",
    "component-compatibility-checker",
    "verificador-compatibilidad-componentes",
    "verificateur-compatibilite-composants",
    "komponenten-kompatibilitaetspruefung",
  ),
  route(
    "psu_calculator",
    "calcolatore-alimentatore",
    "power-supply-calculator",
    "calculadora-fuente-alimentacion",
    "calculateur-alimentation",
    "netzteil-rechner",
  ),
  route(
    "ups_runtime",
    "calcolatore-autonomia-ups",
    "ups-runtime-calculator",
    "calculadora-autonomia-sai",
    "calculateur-autonomie-onduleur",
    "usv-laufzeit-rechner",
  ),
  route(
    "wifi_coverage",
    "verifica-copertura-wifi",
    "wifi-coverage-checker",
    "verificador-cobertura-wifi",
    "verificateur-couverture-wifi",
    "wlan-abdeckungscheck",
  ),
  route(
    "office_network",
    "configuratore-rete-ufficio",
    "office-network-configurator",
    "configurador-red-oficina",
    "configurateur-reseau-bureau",
    "buero-netzwerk-konfigurator",
  ),
  route(
    "backup_calculator",
    "calcolatore-backup",
    "backup-calculator",
    "calculadora-backup",
    "calculateur-sauvegarde",
    "backup-rechner",
  ),
  route(
    "logo_brief",
    "generatore-brief-logo",
    "logo-brief-generator",
    "generador-brief-logo",
    "generateur-brief-logo",
    "logo-briefing-generator",
  ),
  route(
    "website_brief",
    "generatore-brief-sito-web",
    "website-brief-generator",
    "generador-brief-sitio-web",
    "generateur-brief-site-web",
    "website-briefing-generator",
  ),
  route(
    "seo_content_plan",
    "calcolatore-piano-seo-contenuti",
    "seo-content-plan-calculator",
    "calculadora-plan-seo-contenidos",
    "calculateur-plan-seo-contenus",
    "seo-content-plan-rechner",
  ),
  route(
    "maintenance_calculator",
    "calcolatore-manutenzione-sito",
    "website-maintenance-calculator",
    "calculadora-mantenimiento-web",
    "calculateur-maintenance-site",
    "website-wartungsrechner",
  ),
  route(
    "privacy_cookie_checklist",
    "checklist-privacy-cookie",
    "privacy-cookie-checklist",
    "checklist-privacidad-cookies",
    "checklist-confidentialite-cookies",
    "datenschutz-cookie-checkliste",
  ),
] as const;

export function toolIdFromSlug(
  slug: string,
  locale: SupportedLocale,
): string | null {
  return (
    TOOL_ROUTE_INDEX.find((definition) => definition.slugs[locale] === slug)
      ?.id ?? null
  );
}
